import type {
  CrremCorrectionFlags,
  CrremDashboardPayload,
  CrremDashboardScenario,
} from "features/crrem-pathway-chart/model/types";

import {
  pickDashboardMetric,
  pickScenarioBySettings,
  pickTrendScenarios,
} from "features/crrem-pathway-chart/lib/parseCrremDashboardApi";
import { formatNumberBasedOnUser } from "utils/namesManipulation";

import type {
  CrremSettings,
  EnergyAnalysisCategoryTotal,
  EnergyAnalysisMixCell,
  EnergyAnalysisRow,
  EnergyAnalysisSplitDimension,
} from "../model/types";

import { buildCategoryTotals, collectYears, sumValues } from "./aggregate";
import { buildEnergyAnalysisAxisMax } from "./chartStyle";
import { buildCategoryColorMap } from "./colors";
import { toEnergyAnalysisLabel } from "./labels";

const totalAtYear = (rows: EnergyAnalysisRow[], year: number) =>
  sumValues(rows.filter((row) => row.year === year));

const formatChartNumber = (value: number) => formatNumberBasedOnUser(value, 2);
const commonGrid = { bottom: 44, containLabel: true, left: 12, right: 16, top: 24 };

export const buildChangeWaterfallOption = (
  rows: EnergyAnalysisRow[],
  dimension: EnergyAnalysisSplitDimension,
  unit: string
) => {
  const years = collectYears(rows);
  const startYear = years[0];
  const endYear = years[years.length - 1];
  if (startYear === undefined || endYear === undefined) return {};
  const startRows = rows.filter((row) => row.year === startYear);
  const endRows = rows.filter((row) => row.year === endYear);
  const keys = [...new Set(rows.map((row) => row[dimension]))];
  const changes = keys
    .map((key) => ({
      key,
      label: toEnergyAnalysisLabel(key),
      value:
        sumValues(endRows.filter((row) => row[dimension] === key)) -
        sumValues(startRows.filter((row) => row[dimension] === key)),
    }))
    .filter((item) => Math.abs(item.value) > 0.0001)
    .sort((left, right) => Math.abs(right.value) - Math.abs(left.value));
  const startTotal = sumValues(startRows);
  const endTotal = sumValues(endRows);
  let runningTotal = startTotal;
  const helpers = [0];
  const values: { itemStyle: { color: string }; raw: number; value: number }[] = [
    { itemStyle: { color: "#20263f" }, raw: startTotal, value: startTotal },
  ];
  changes.forEach((change) => {
    const helper = change.value >= 0 ? runningTotal : runningTotal + change.value;
    helpers.push(helper);
    values.push({
      itemStyle: { color: change.value >= 0 ? "#bd5c43" : "#4f9a6d" },
      raw: change.value,
      value: Math.abs(change.value),
    });
    runningTotal += change.value;
  });
  helpers.push(0);
  values.push({ itemStyle: { color: "#3f748c" }, raw: endTotal, value: endTotal });
  return {
    dataZoom: [],
    grid: { bottom: 90, containLabel: false, left: 72, right: 28, top: 36 },
    series: [
      {
        data: helpers,
        emphasis: { disabled: true },
        itemStyle: { borderColor: "transparent", color: "transparent" },
        stack: "waterfall",
        tooltip: { show: false },
        type: "bar",
      },
      {
        data: values,
        label: {
          formatter: ({ data }: { data: { raw: number } }) =>
            `${data.raw > 0 && data.raw !== startTotal && data.raw !== endTotal ? "+" : ""}${formatChartNumber(data.raw)}`,
          position: "top",
          show: true,
        },
        name: unit,
        stack: "waterfall",
        type: "bar",
      },
    ],
    tooltip: {
      formatter: ({ data, name }: { data: { raw: number }; name: string }) =>
        `${name}: ${formatChartNumber(data.raw)} ${unit}`,
      trigger: "item",
    },
    xAxis: [
      {
        axisLabel: { align: "right", interval: 0, margin: 12, rotate: 45 },
        boundaryGap: true,
        data: [`${startYear}`, ...changes.map((item) => item.label), `${endYear}`],
        type: "category",
      },
    ],
    yAxis: [{ axisLabel: { formatter: formatChartNumber }, type: "value" }],
  };
};

export const buildComparisonDoughnutOption = (
  rows: EnergyAnalysisRow[],
  dimension: EnergyAnalysisSplitDimension,
  unit: string
) => {
  const years = collectYears(rows);
  const startYear = years[0];
  const endYear = years[years.length - 1];
  if (startYear === undefined || endYear === undefined) return {};
  const startTotals = buildCategoryTotals(
    rows.filter((row) => row.year === startYear),
    dimension
  );
  const endTotals = buildCategoryTotals(
    rows.filter((row) => row.year === endYear),
    dimension
  );
  const toData = (totals: EnergyAnalysisCategoryTotal[]) =>
    totals.map((item) => ({ itemStyle: { color: item.color }, name: item.label, value: item.value }));
  const centerLabel = (year: number, total: number, left: string) => ({
    left,
    style: {
      rich: {
        total: { fontSize: 22, fontWeight: 700, lineHeight: 30 },
        unit: { color: "#687086", fontSize: 11, lineHeight: 18 },
      },
      text: `{total|${formatChartNumber(total)}}\n{unit|${unit} in ${year}}`,
      textAlign: "center",
      top: "42%",
    },
    type: "text",
  });
  return {
    dataZoom: [],
    graphic: [
      centerLabel(startYear, totalAtYear(rows, startYear), "25%"),
      centerLabel(endYear, totalAtYear(rows, endYear), "75%"),
    ],
    legend: { bottom: 0, type: "scroll" },
    series: [
      { center: ["25%", "48%"], data: toData(startTotals), radius: ["35%", "55%"], type: "pie" },
      { center: ["75%", "48%"], data: toData(endTotals), radius: ["35%", "55%"], type: "pie" },
    ],
    tooltip: { trigger: "item", valueFormatter: formatChartNumber },
  };
};

export const buildStackedAreaOption = (
  rows: EnergyAnalysisRow[],
  dimension: EnergyAnalysisSplitDimension,
  unit: string
) => {
  const years = collectYears(rows);
  const keys = [...new Set(rows.map((row) => row[dimension]))].sort();
  const colors = buildCategoryColorMap(keys);
  const totals = years.map((year) => totalAtYear(rows, year));
  return {
    dataZoom: [],
    grid: commonGrid,
    legend: { bottom: 0, type: "scroll" },
    series: keys.map((key) => ({
      areaStyle: {},
      data: years.map((year, index) => {
        const value = sumValues(rows.filter((row) => row.year === year && row[dimension] === key));
        return totals[index] ? (value / (totals[index] ?? 1)) * 100 : 0;
      }),
      emphasis: { focus: "series" },
      itemStyle: { color: colors[key] },
      lineStyle: { width: 1 },
      name: toEnergyAnalysisLabel(key),
      stack: "share",
      type: "line",
    })),
    tooltip: { trigger: "axis", valueFormatter: (value: number) => `${formatChartNumber(value)}%` },
    xAxis: [{ boundaryGap: false, data: years, type: "category" }],
    yAxis: [
      {
        axisLabel: { formatter: (value: number) => `${value}%` },
        max: 100,
        name: `Share of ${unit}`,
        type: "value",
      },
    ],
  };
};

export const buildSankeyOption = (cells: EnergyAnalysisMixCell[], unit: string) => {
  const useTypes = [...new Set(cells.map((cell) => cell.useType))];
  const utilities = [...new Set(cells.map((cell) => cell.utility))];
  const useTypeColors = buildCategoryColorMap(useTypes);
  const utilityColors = buildCategoryColorMap(utilities);
  return {
    dataZoom: [],
    series: [
      {
        data: [
          ...useTypes.map((key) => ({
            displayName: toEnergyAnalysisLabel(key),
            itemStyle: { color: useTypeColors[key] },
            label: { align: "right", position: "left" },
            name: `use:${key}`,
          })),
          ...utilities.map((key) => ({
            displayName: toEnergyAnalysisLabel(key),
            itemStyle: { color: utilityColors[key] },
            label: { align: "left", position: "right" },
            name: `utility:${key}`,
          })),
        ],
        emphasis: { focus: "adjacency" },
        label: {
          formatter: ({ data }: { data: { displayName: string } }) => data.displayName,
          overflow: "truncate",
          width: 88,
        },
        layoutIterations: 0,
        left: "24%",
        lineStyle: { color: "gradient", curveness: 0.5, opacity: 0.45 },
        links: cells.map((cell) => ({
          source: `use:${cell.useType}`,
          target: `utility:${cell.utility}`,
          value: cell.value,
        })),
        nodeAlign: "justify",
        nodeGap: 10,
        right: "24%",
        type: "sankey",
      },
    ],
    tooltip: {
      formatter: ({ data, dataType }: { data: { displayName?: string; value?: number }; dataType: string }) =>
        dataType === "edge"
          ? `${formatChartNumber(data.value ?? 0)} ${unit}`
          : (data.displayName ?? ""),
      trigger: "item",
    },
  };
};

const sameCorrections = (left: CrremCorrectionFlags, right: Partial<CrremCorrectionFlags>) =>
  Object.entries(right).every(([key, enabled]) => left[key as keyof CrremCorrectionFlags] === enabled);

const scenarioTotal = (scenario: CrremDashboardScenario, settings: CrremSettings, year: number) =>
  totalAtYear(pickDashboardMetric(scenario, settings.displayMetric).rows, year);

const milestoneYears = (rows: EnergyAnalysisRow[]) => {
  const years = collectYears(rows);
  const first = years[0];
  const last = years[years.length - 1];
  return years.filter((year) => year === first || year === last || year % 10 === 0);
};

export const buildCorrectionScenariosTrendOption = (
  payload: CrremDashboardPayload,
  settings: CrremSettings,
  unit: string,
  filteredYears?: number[]
) => {
  const scenarios = pickTrendScenarios(payload, settings.trendMethod);
  const selected = pickScenarioBySettings(scenarios, settings);
  const scenarioRows = scenarios.map((scenario) => ({
    rows: pickDashboardMetric(scenario, settings.displayMetric).rows,
    scenario,
  }));
  const availableYears = collectYears(scenarioRows.flatMap((item) => item.rows));
  const filteredYearSet = filteredYears ? new Set(filteredYears) : undefined;
  const years = filteredYearSet
    ? availableYears.filter((year) => filteredYearSet.has(year))
    : availableYears;
  const colors = buildCategoryColorMap(scenarios.map((scenario) => scenario.id));
  return {
    dataZoom: [],
    series: scenarioRows.map(({ rows, scenario }) => {
      const isSelected = scenario.id === selected?.id;
      return {
        data: years.map((year) => totalAtYear(rows, year)),
        emphasis: { focus: "series" },
        itemStyle: { color: colors[scenario.id] },
        lineStyle: { opacity: isSelected ? 1 : 0.7, width: isSelected ? 4 : 2 },
        name: scenario.label,
        symbol: isSelected ? "circle" : "none",
        type: "line",
      };
    }),
    tooltip: {
      trigger: "axis",
      valueFormatter: (value: number) => `${formatChartNumber(value)} ${unit}`,
    },
    xAxis: [{ data: years, type: "category" }],
    yAxis: [{ axisLabel: { formatter: formatChartNumber }, name: unit, type: "value" }],
  };
};

export const buildScenarioHeatmapOption = (payload: CrremDashboardPayload, settings: CrremSettings) => {
  const scenarios = pickTrendScenarios(payload, settings.trendMethod);
  const selected = pickScenarioBySettings(scenarios, settings);
  const years = milestoneYears(selected ? pickDashboardMetric(selected, settings.displayMetric).rows : []);
  const values = scenarios.flatMap((scenario, yIndex) =>
    years.map((year, xIndex) => [xIndex, yIndex, scenarioTotal(scenario, settings, year)])
  );
  const max = values.reduce((result, item) => Math.max(result, item[2] ?? 0), 0);
  return {
    dataZoom: [],
    grid: { ...commonGrid, left: 16 },
    series: [
      {
        data: values,
        label: {
          formatter: ({ value }: { value: number[] }) => formatChartNumber(value[2] ?? 0),
          show: true,
        },
        type: "heatmap",
      },
    ],
    tooltip: {
      formatter: ({ value }: { value: number[] }) => {
        const scenario = scenarios[value[1] ?? 0];
        const year = years[value[0] ?? 0];
        return `${scenario?.label ?? ""}, ${year}: ${formatChartNumber(value[2] ?? 0)}`;
      },
      trigger: "item",
    },
    visualMap: {
      bottom: 0,
      inRange: { color: ["#b8e2c1", "#80c8d6", "#6177e6", "#7135d8"] },
      left: "center",
      max,
      min: 0,
      orient: "horizontal",
    },
    xAxis: [{ boundaryGap: true, data: years, type: "category" }],
    yAxis: [{ boundaryGap: true, data: scenarios.map((scenario) => scenario.label), type: "category" }],
  };
};

export const buildCorrectionImpactOption = (
  payload: CrremDashboardPayload,
  settings: CrremSettings,
  snapshotYear?: number
) => {
  const scenarios = pickTrendScenarios(payload, settings.trendMethod);
  const allRows = scenarios.flatMap(
    (scenario) => pickDashboardMetric(scenario, settings.displayMetric).rows
  );
  const years = collectYears(allRows);
  const year = snapshotYear !== undefined && years.includes(snapshotYear) ? snapshotYear : years[years.length - 1];
  const baseline = scenarios.find((scenario) =>
    sameCorrections(scenario.corrections, { climate: false, coverage: false, vacancy: false })
  );
  const baselineValue = baseline && year !== undefined ? scenarioTotal(baseline, settings, year) : 0;
  const unit = baseline ? pickDashboardMetric(baseline, settings.displayMetric).unit : "";
  const colors = buildCategoryColorMap(scenarios.map((scenario) => scenario.id));
  const scenarioChanges = scenarios
    .map((scenario) => ({
      id: scenario.id,
      label: scenario.label,
      value: year === undefined ? 0 : scenarioTotal(scenario, settings, year) - baselineValue,
    }))
    .reverse();
  const allChanges = years.flatMap((candidateYear) => {
    const candidateBaseline = baseline ? scenarioTotal(baseline, settings, candidateYear) : 0;
    return scenarios.map(
      (scenario) => scenarioTotal(scenario, settings, candidateYear) - candidateBaseline
    );
  });
  const range = buildEnergyAnalysisAxisMax(
    Math.max(...allChanges.map((value) => Math.abs(value)), 0)
  );
  return {
    dataZoom: [],
    series: [
      {
        barMaxWidth: 42,
        data: scenarioChanges.map((item) => ({
          itemStyle: { color: colors[item.id] },
          value: item.value,
        })),
        name: "Change from measured",
        type: "bar",
      },
    ],
    tooltip: {
      trigger: "axis",
      valueFormatter: (value: number) => `${formatChartNumber(value)} ${unit}`,
    },
    xAxis: [
      {
        axisLabel: { formatter: formatChartNumber },
        interval: range.interval,
        max: range.max,
        min: allChanges.some((value) => value < 0) ? -range.max : 0,
        type: "value",
      },
    ],
    yAxis: [
      {
        boundaryGap: true,
        data: scenarioChanges.map((item) => item.label),
        type: "category",
      },
    ],
  };
};

export const buildCorrectionWaterfallOption = (
  payload: CrremDashboardPayload,
  settings: CrremSettings,
  unit: string,
  snapshotYear?: number
) => {
  const scenarios = pickTrendScenarios(payload, settings.trendMethod);
  const allRows = scenarios.flatMap((scenario) => pickDashboardMetric(scenario, settings.displayMetric).rows);
  const years = collectYears(allRows);
  const year = snapshotYear !== undefined && years.includes(snapshotYear) ? snapshotYear : years[years.length - 1];
  if (year === undefined) return {};
  const baseline = scenarios.find((scenario) =>
    sameCorrections(scenario.corrections, { climate: false, coverage: false, vacancy: false })
  );
  const ordered = [
    { label: "+ Coverage", match: { climate: false, coverage: true, vacancy: false } },
    { label: "+ Vacancy", match: { climate: false, coverage: true, vacancy: true } },
    { label: "+ Climate", match: { climate: true, coverage: true, vacancy: true } },
  ];
  const baselineValue = baseline ? scenarioTotal(baseline, settings, year) : 0;
  const points = ordered
    .map((item) => {
      const scenario = scenarios.find((candidate) => sameCorrections(candidate.corrections, item.match));
      return scenario ? { label: item.label, value: scenarioTotal(scenario, settings, year) } : undefined;
    })
    .filter((item): item is { label: string; value: number } => Boolean(item));
  const labels = ["Measured", ...points.map((point) => point.label), "Fully corrected"];
  const deltas = points.map((point, index) => point.value - (index === 0 ? baselineValue : points[index - 1]!.value));
  let running = baselineValue;
  const helpers = [0];
  const data = [
    { itemStyle: { color: "#20263f" }, raw: baselineValue, value: baselineValue },
    ...deltas.map((delta) => {
      helpers.push(delta >= 0 ? running : running + delta);
      running += delta;
      return { itemStyle: { color: delta >= 0 ? "#bd5c43" : "#4f9a6d" }, raw: delta, value: Math.abs(delta) };
    }),
    { itemStyle: { color: "#3f748c" }, raw: running, value: running },
  ];
  helpers.push(0);
  const range = buildEnergyAnalysisAxisMax(
    Math.max(
      ...scenarios.flatMap((scenario) =>
        years.map((candidateYear) => scenarioTotal(scenario, settings, candidateYear))
      ),
      0
    )
  );
  return {
    dataZoom: [],
    grid: { bottom: 90, containLabel: false, left: 72, right: 28, top: 36 },
    series: [
      {
        data: helpers,
        itemStyle: { borderColor: "transparent", color: "transparent" },
        stack: "correction",
        tooltip: { show: false },
        type: "bar",
      },
      {
        data,
        label: {
          formatter: ({ data: item }: { data: { raw: number } }) => formatChartNumber(item.raw),
          position: "top",
          show: true,
        },
        stack: "correction",
        type: "bar",
      },
    ],
    tooltip: {
      formatter: ({ data: item, name }: { data: { raw: number }; name: string }) =>
        `${name}: ${formatChartNumber(item.raw)} ${unit}`,
      trigger: "item",
    },
    xAxis: [
      {
        axisLabel: { align: "right", interval: 0, margin: 12, rotate: 45 },
        boundaryGap: true,
        data: labels,
        type: "category",
      },
    ],
    yAxis: [
      {
        axisLabel: { formatter: formatChartNumber },
        interval: range.interval,
        max: range.max,
        min: 0,
        type: "value",
      },
    ],
  };
};

export const buildFactorOption = (payload: CrremDashboardPayload, settings: CrremSettings) => {
  const scenario = pickScenarioBySettings(pickTrendScenarios(payload, settings.trendMethod), settings);
  if (!scenario) return undefined;
  const energyRows = scenario.energy.rows;
  const co2Rows = scenario.co2.rows;
  const years = collectYears([...energyRows, ...co2Rows]);
  const utilities = [...new Set([...energyRows, ...co2Rows].map((row) => row.utility))].sort();
  const colors = buildCategoryColorMap(utilities);
  const ratio = (utility: string | undefined, year: number) => {
    const filter = (row: EnergyAnalysisRow) => row.year === year && (!utility || row.utility === utility);
    const energy = sumValues(energyRows.filter(filter));
    const co2 = sumValues(co2Rows.filter(filter));
    return energy > 0 ? co2 / energy : null;
  };
  return {
    dataZoom: [],
    grid: commonGrid,
    legend: { bottom: 0, type: "scroll" },
    series: [
      {
        data: years.map((year) => ratio(undefined, year)),
        itemStyle: { color: "#20263f" },
        lineStyle: { width: 3 },
        name: "Building average",
        type: "line",
      },
      ...utilities.map((utility) => ({
        data: years.map((year) => ratio(utility, year)),
        itemStyle: { color: colors[utility] },
        name: toEnergyAnalysisLabel(utility),
        type: "line",
      })),
    ],
    tooltip: { trigger: "axis" },
    xAxis: [{ data: years, type: "category" }],
    yAxis: [{ name: "kgCO₂ per kWh", type: "value" }],
  };
};

export const buildTargetGap = (payload: CrremDashboardPayload, settings: CrremSettings, rows: EnergyAnalysisRow[]) => {
  const floorArea = payload.floorAreaM2;
  const years = collectYears(rows);
  const year = years[years.length - 1];
  const pathway = settings.displayMetric === "kwh" ? payload.pathways.energy : payload.pathways.co2;
  const target =
    pathway.target_1_5.find((point) => point.year === year) ??
    pathway.target_1_5.reduce<(typeof pathway.target_1_5)[number] | undefined>(
      (closest, point) =>
        year !== undefined && (!closest || Math.abs(point.year - year) < Math.abs(closest.year - year)) ? point : closest,
      undefined
    );
  if (!floorArea || year === undefined || !target) return undefined;
  const scenarioValue = totalAtYear(rows, year) / floorArea;
  return {
    gap: scenarioValue - target.value,
    scenarioValue,
    targetValue: target.value,
    unit: pathway.unit,
    year,
  };
};
